import { CheckCircle, Error, Pending } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Divider, Typography } from "@mui/material";
import React, { useState } from "react";
import { ApprovalStatus } from "../services/ApprovalService";
import { createLogger } from "../utils/logger";

const logger = createLogger("PendingApprovalScreen");

interface PendingApprovalScreenProps {
  approvalStatus: ApprovalStatus;
  onRefetch: () => Promise<void>;
}

export const PendingApprovalScreen: React.FC<PendingApprovalScreenProps> = ({ approvalStatus, onRefetch }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefetch();
    } catch (error) {
      logger.error("Error refreshing approval status:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusIcon = () => {
    switch (approvalStatus.approvalStatus) {
      case "approved":
        return <CheckCircle color="success" sx={{ fontSize: 48 }} />;
      case "pending":
        return <Pending color="warning" sx={{ fontSize: 48 }} />;
      case "rejected":
        return <Error color="error" sx={{ fontSize: 48 }} />;
      default:
        return <Pending color="warning" sx={{ fontSize: 48 }} />;
    }
  };

  const getStatusColor = (): "success" | "warning" | "error" => {
    switch (approvalStatus.approvalStatus) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      default:
        return "warning";
    }
  };

  const getStatusMessage = () => {
    switch (approvalStatus.approvalStatus) {
      case "approved":
        return "Your account has been approved! You can now access the application.";
      case "pending":
        return "Your account is pending approval. We'll review your registration and get back to you soon.";
      case "rejected":
        return "Your account has been rejected. Please contact support for assistance.";
      default:
        return "Your account status is being reviewed.";
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 600, width: "100%" }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: "center", mb: 3 }}>
            {getStatusIcon()}
            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
              Account Status
            </Typography>
            <Typography variant="h6" color={`${getStatusColor()}.main`} gutterBottom>
              {approvalStatus.approvalStatus.toUpperCase()}
            </Typography>
          </Box>

          <Alert severity={getStatusColor()} sx={{ mb: 3 }}>
            {getStatusMessage()}
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Email:</strong> {approvalStatus.email}
            </Typography>
            {approvalStatus.displayName && (
              <Typography variant="body2" color="text.secondary">
                <strong>Name:</strong> {approvalStatus.displayName}
              </Typography>
            )}
          </Box>

          {approvalStatus.approvalStatus === "pending" && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Help Us Process Your Request
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                You can help us process your request faster by providing additional information through our registration form:
              </Typography>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => {
                  logger.info("User opening Google Form for registration interest", { email: approvalStatus.email });
                  window.open("https://docs.google.com/forms/d/e/1FAIpQLSc4fVxKMVrg896mVaqMY6wjilqY0q9slKdyz8gY7ERG4j4zLA/viewform?usp=sharing&ouid=115756444011604720407", "_blank");
                }}
                sx={{ mb: 2 }}
              >
                Complete Registration Form
              </Button>
            </>
          )}

          {approvalStatus.approvalStatus === "rejected" && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                Need Help?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                If you believe this was an error or would like to appeal the decision, please contact our support team.
              </Typography>
              <Button variant="contained" color="primary" href="mailto:support@example.com" sx={{ mr: 2 }}>
                Contact Support
              </Button>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button variant="outlined" onClick={handleRefresh} disabled={isRefreshing} startIcon={isRefreshing ? <CircularProgress size={16} /> : undefined}>
              {isRefreshing ? "Checking..." : "Check Status"}
            </Button>
            <Button
              variant="text"
              onClick={async () => {
                try {
                  logger.info("User requested logout from pending approval screen");
                  const { signOutUser } = await import("../services/firebase");
                  await signOutUser();
                } catch (error) {
                  logger.error("Error signing out:", error);
                }
              }}
            >
              Sign Out
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
